import ErrorComponent from "@/components/atoms/ErrorComponent";
import Loader from "@/components/atoms/Loader";
import Select from "@/components/atoms/Select";
import {
  INVALID_FORM_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_NAME_MESSAGE,
} from "@/constants/validatorMessages";
import getDifferenceBetweenTwoObjects from "@/helpers/FormHelpers";
import { CardDetailsProps } from "@/interfaces/Card";
import CompanyStatus, {
  UpdateCompanyStatusForm,
} from "@/interfaces/CompanyStatus";
import { USER_ROLES } from "@/interfaces/IUser";
import {
  useGetCompanyStatusQuery,
  useUpdateCompanyStatusMutation,
} from "@/services/companies/statuses";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { FC, useEffect } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";
import CardHeader from "../../card-headers/CardHeader";
import { useNavigate } from "react-router-dom";

const CompanyStatusEditCard: FC<CardDetailsProps<CompanyStatus>> = ({ id }) => {
  const navigation = useNavigate();
  const { data, isLoading, isError, error } = useGetCompanyStatusQuery(id);
  const [updateCompanyStatusAsync] = useUpdateCompanyStatusMutation();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateCompanyStatusForm>();

  useEffect(() => {
    data &&
      reset({
        ...data,
        role: USER_ROLES.find((role) => role.value === data.role),
      });
  }, [data]);

  const onSubmit: SubmitHandler<UpdateCompanyStatusForm> = async (newData) => {
    const result = getDifferenceBetweenTwoObjects<UpdateCompanyStatusForm>(
      data,
      newData
    );
    await toast.promise(
      updateCompanyStatusAsync([
        id,
        {
          ...result,
          role: result.role?.value,
        },
      ]).unwrap(),
      {
        pending: "Modification du statut de l'entreprise en cours ...",
        success: {
          render() {
            navigation(-1);
            return (
              <p className="mb-0 text-center">
                Le statut de l'entreprise à bien été modifiée 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Vérifiez que les champs sont correctement remplis et que le nom de l'utilisateur n'existe pas déjà";
            }
            return "Une erreur est survenue";
          },
        },
      }
    );
  };

  const onError: SubmitErrorHandler<UpdateCompanyStatusForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit, onError)}>
      <Card>
        <CardHeader title="Modification du statut de l'entreprise" />
        <Card.Body>
          <Row className="gx-3">
            <Col lg={6}>
              <Controller
                control={control}
                name="backgroundColor"
                render={({ field: { value: backgroundColor } }) => (
                  <Controller
                    control={control}
                    name="textColor"
                    render={({ field: { value: color } }) => (
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Nom : <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          placeholder="ex : En attente"
                          type="text"
                          style={{ backgroundColor, color }}
                          {...register("name", {
                            required: {
                              value: true,
                              message: INVALID_NAME_MESSAGE,
                            },
                            minLength: {
                              value: 2,
                              message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                            },
                            maxLength: {
                              value: 255,
                              message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                            },
                          })}
                          isInvalid={!!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    )}
                  />
                )}
              />
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Pour le role :</Form.Label>
                <Controller
                  control={control}
                  name="role"
                  render={({ field }) => (
                    <Select
                      options={USER_ROLES}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.role?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Séléctionne la couleur du texte{" "}
                  <span className="text-danger">*</span> :
                </Form.Label>
                <Form.Control
                  type="color"
                  {...register("textColor", {
                    required: "Ce champ est obligatoire",
                  })}
                  className="w-100"
                  isInvalid={!!errors.textColor}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.textColor?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Séléctionne la couleur de fond{" "}
                  <span className="text-danger">*</span> :
                </Form.Label>

                <Form.Control
                  type="color"
                  {...register("backgroundColor", {
                    required: "Ce champ est obligatoire",
                  })}
                  className="w-100"
                  isInvalid={!!errors.backgroundColor}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.backgroundColor?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Button
            type="submit"
            variant="success"
            disabled={isLoading}
            className="mt-3 me-3"
          >
            Confirmer
          </Button>
        </Card.Body>
      </Card>
    </Form>
  );
};

export default CompanyStatusEditCard;
